import { useState } from "react"
import Input from "@/v2-ui/input"
import Textarea from "@/v2-ui/textarea"
import Button from "@/v2-backoffice-client/components/button"

export type FeatureDetailsHotfixTextsProps = {
  name: string
  description: string
  isSaving: boolean
  featureCodeLabel: string
  onSave: (texts: { name: string; description: string | null }) => void
}

/**
 * Prod deployments have no authoring/translation DB: the runtime's en-GB
 * text is the only editable surface, as an explicit emergency-hotfix path.
 *
 * The save itself belongs to the catalog that owns the row, so the caller
 * passes its own update mutation in — this component only owns the draft.
 */
function FeatureDetailsHotfixTexts(props: FeatureDetailsHotfixTextsProps) {
  const { name: savedName, description: savedDescription } = props
  const { isSaving, featureCodeLabel, onSave } = props

  const [name, setName] = useState(savedName)
  const [description, setDescription] = useState(savedDescription)

  const isDirty = name !== savedName || description !== savedDescription

  return (
    <section className="flex flex-col gap-2">
      <header className="text-sm font-medium">Name & description</header>
      <p className="text-xs text-muted-foreground">
        Hotfix mode: this deployment has no translation catalog, so this save
        lands on the live runtime only (en-GB) and the next catalog publish
        overwrites it.
      </p>
      <div className="flex max-w-xl flex-col gap-1.5">
        <Input
          aria-label="Name (en-GB)"
          placeholder="Name"
          required
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <Textarea
          aria-label="Description (en-GB)"
          placeholder="Description"
          rows={3}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </div>
      <div>
        <Button
          size="sm"
          isLoading={isSaving}
          disabled={!isDirty || name.trim() === ""}
          data-feature-code={`UI_Backoffice${featureCodeLabel}_Details_Save`}
          onClick={() => onSave({ name, description: description || null })}
        >
          Save texts
        </Button>
      </div>
    </section>
  )
}

export default FeatureDetailsHotfixTexts
